import { ErrorHandler, Injectable, Injector, OnInit } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Observable, throwError, EMPTY, from, forkJoin } from 'rxjs';
import { catchError, finalize, map, switchMap } from 'rxjs/operators';
import { AuthEventService } from '../services/auth-event.service';
import { DeviceService } from '../utils/device.service';
import { Router } from '@angular/router';
import { DataService } from '../services/data.service';
import { InfoModalService } from '../services/info-modal.service';
import { MustMatch } from '../helpers/must-match.validator';
import { StorageService } from '../services/storage.service';
import { ErrorHandlingService } from '../services/error-handling.service';
import { BackendErrorCode } from '../models/error.model';
import { GapSettings } from '../config/gapsettings';


/**
 * HTTP Interceptor for handling authentication tokens, device headers, and errors
 * ADDS TO EVERY REQUEST:
 * - Authorization: Bearer {token}
 * - x-device-id: {deviceId/uuid}
 * - x-device-fingerprint: {fingerprint}
 * - x-pathway-id: {pathwayId} (when user is logged in)
 */
@Injectable()
export class HttpConfigInterceptor implements HttpInterceptor {
  timer;
  constructor(
    public router: Router,
    public data: DataService,
    private authEventService: AuthEventService,
    private infoModal: InfoModalService,
    private deviceService: DeviceService,
    private storage: StorageService,
    /** Lazy-resolve ErrorHandlingService to avoid HttpClient ↔ interceptor cycles. */
    private injector: Injector,
  ) {
  }

  private hasStoredAuthToken(): boolean {
    const token = this.storage.getToken?.() ?? null;
    return !!(token && String(token).trim() !== '');
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (this.data.networkConnection != undefined && this.data.networkConnection.connected == false) {
      return EMPTY;
    }

    // Combine both async operations into a single switchMap
    return forkJoin({
      deviceInfo: from(this.deviceService.getDeviceInfo()),
      token: from(this.storage.getTokenAsync())
    }).pipe(
      switchMap(({ deviceInfo, token }) => {
        const headers: { [key: string]: string } = {};
        const appLanguage = localStorage.getItem('app_language') === 'cy' ? 'cy' : 'en';

        // Add device ID header
        if (deviceInfo?.deviceId) {
          headers['X-Device-Id'] = deviceInfo.deviceId;
        }

        // Add device fingerprint header
        if (deviceInfo?.deviceFingerprint) {
          headers['X-Device-Fingerprint'] = deviceInfo.deviceFingerprint;
        }

        // Add pathway ID header
        if (this.data.pathway?.id) {
          headers['X-Pathway-Id'] = this.data.pathway.id.toString();
        }

        const isNhsStatecode = request.url.includes('nhs/statecode/');
        const loggedInForState =
          localStorage.getItem('isUserLogged') === 'true' || this.data.isUserLogged;
        if (token && (!isNhsStatecode || loggedInForState)) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        // Advertise the currently selected UI language so backend payload translation
        // stays aligned with the app language before and after login.
        headers['X-Language'] = appLanguage;

        // Clone request with all headers at once
        if (Object.keys(headers).length > 0) {
          request = request.clone({ setHeaders: headers });
        }

        return next.handle(request);
      }),
      map((event: HttpEvent<any>) => {
        if (event instanceof HttpResponse && !!event.body) {
          //console.log('event--->>>', event);
          // this.errorDialogService.openDialog(event);
          const { url, body } = event;

          // Handle app update
          if (body.app_update && url.indexOf('userappnav') !== -1 && this.data.routerHistory.length > 1) {
            console.log('update available');
            this.data.goToUpdate(body.app_update.title, body.app_update.subtitle, body.app_update.force);

            // if we have user from backend, update local user data
            if (body.user) {
              this.data.user = body.user;
            }
          }

          // Handle user-related reload app
          if (['/fakesplash', '/accesscodes'].indexOf(this.router.url) === -1 &&
            url.indexOf('userappnav') !== -1 &&
            body.user?.reload_app === '1') {

            this.data.setUserSettings([{ reload_app: '0' }]);
            this.data.presentAlert('Account updated', 'Your account has been updated. Please re-select the condition.', 'OK', '');
          }

          // Feature flags only. The API host is set from the guest page
          // and the user settings page, never from a response.
          if (url && (
            url.indexOf('usernavguest') !== -1
            || url.indexOf('userappnav') !== -1
            || url.indexOf('pulse') !== -1
          )) {
            void this.data.gapsettings.updateSettingsFromUrlEventBody(flagsOnly(body), url);
          }
          
        }
        return event;
      }),
      finalize(() => {
        // Any cleanup or final actions can be performed here
        // For example, hiding a loading spinner
        this.data.showAjaxRequestLoader = false;

        // for debugging purposes
        // Device.getInfo().then(info => {
        //   console.log('Device info:', info);
        // });

        // for example, logging
        // console.log('Request to ' + request.url + ' completed.');

        // for example, resetting state
        // this.isLoading = false;

      }),
      catchError((error: HttpErrorResponse) => {
        const settingsApply = (error.error && typeof error.error === 'object')
          ? this.data.gapsettings.updateSettingsFromUrlEventBody(flagsOnly(error.error), request.url)
          : Promise.resolve();

        return from(settingsApply).pipe(switchMap(() => {
        console.log('error--->>>', error);
        console.log('status--->>>', error.status);
        console.log('requestUrl---->>>', request.url);

        // read special backend headers
        const ignoreAlert = error.headers?.get?.('X-Ignore-Alert') === 'true';
        const skipRefresh = error.headers?.get?.('X-Skip-Refresh') === 'true';

        // normalize requested route for condition checks
        const normalizedReqUrl = normalizeUrl(request.url, this.data._basePath || '');

        // if from backend comes to skipCheck = true, skip the error handling
        const skipCheck = error.headers.get('X-Skip-Check') === 'true';
        if (skipCheck) {
          return throwError(() => error);
        }

        // Create/link return their own message (existing email, expired NHS session).
        // Turning that into an empty stream becomes "no elements in sequence" and
        // the page never stores the login token.
        if (request.url.includes('nhs/create-account') || request.url.includes('nhs/merge-account')) {
          return throwError(() => error);
        }

        // Account revalidation — gap users must complete MFA again (GT-1412: preserve MFA URL, avoid logout loop on resume)
        if (error.status === 401 && isAccountRevalidationRequired(error)) {
          this.data.showAjaxRequestLoader = false;
          const currentUrl = this.router.url || '';
          const loginMfaPending = this.data.loginResponse?.mfaAuth === true
            && !this.data.mfaAuth.isMfaValidated();
          // Already on MFA page or mid login-MFA flow — keep token, do not boot to /login
          if (currentUrl.includes('/enter-code') || currentUrl.includes('/verify') || loginMfaPending) {
            console.log('[MFA Guard] revalidation_required during MFA flow, staying put', currentUrl);
            return EMPTY;
          }
          const mfaEnabled = localStorage.getItem('enable_login_mfa') === '1'
            || this.data.gapsettings?.enable_login_mfa === '1';
          if (!mfaEnabled) {
            this.router.navigateByUrl('/login');
            return EMPTY;
          }
          this.data.mfaFlow = 'login';
          const target = '/enter-code/login/phone';
          console.log('[MFA Guard] revalidation_required -> navigating to', target);
          this.router.navigateByUrl(target);
          return EMPTY;
        }

        const maintenanceFromSettings = GapSettings.extractSettingsPayload(error.error)?.maintenance_mode;
        if (error.status == 503 || maintenanceFromSettings === true || maintenanceFromSettings === '1') {
          this.data.maintenance_mode = true;
        }

        // Wrong password / signup / MFA code failures are also HTTP 401 — let the
        // page catch them. Only treat remaining 401s as session/token expiry.
        if (error.status === 401 && isCredentialAuthFailure(error, normalizedReqUrl, request.url)) {
          return throwError(() => error);
        }

        // Session/auth-token expiry: one orange guest warning; swallow so
        // DataService subscribers do not stack "Unable to connect" alerts.
        if (error.status === 401) {
          // Not session expiry: already on /guest (e.g. QA gear health probes), or
          // no stored auth token — a 401 without a session cannot be "session expired".
          // Rethrow like credential failures so callers keep their own error handling.
          if (isOnGuestPage(this.router.url) || !this.hasStoredAuthToken()) {
            return throwError(() => error);
          }

          this.data.showAjaxRequestLoader = false;
          this.data.isUserLogged = false;
          this.data.token = null;
          void this.storage.clearAllAuthData();

          const errorHandling = this.injector.get(ErrorHandlingService);
          // Drop any race-opened App Support modal from parallel non-401 failures.
          void this.infoModal.dismissAll();
          errorHandling.sendCustomWarning('/guest', BackendErrorCode.SESSION_EXPIRED);
          return EMPTY;
        }

        // 403: keep pulse-refresh / fakesplash recovery (product case differs from session expiry)
        if (error.status === 403 && normalizedReqUrl.indexOf('/pulse') !== -1) {
          // New NHS accounts are blocked from pulse until the agreement form is saved.
          // That 403 is not a logged-out session — clearing the token here is what
          // made the agreement save look like it had no token.
          const pulseError = error.error as { error_code?: string } | undefined;
          if (pulseError?.error_code === 'nhs_agreement_required') {
            if (!this.router.url.includes('/nhs/account/agreement')) {
              void this.router.navigateByUrl('/nhs/account/agreement?origin=new-account');
            }
            return EMPTY;
          }

          console.log('Forbidden on pulse - Status:', error.status, 'URL:', request.url);

          this.data.showAjaxRequestLoader = false;
          this.data.isUserLogged = false;
          this.data.token = null;
          void this.storage.clearAllAuthData();

          this.router.navigate(['/guest'], { queryParams: { emitEvent: 'forbidden' } });
          return EMPTY;
        } else if (error.status === 403) {
          // Attempt a background pulse refresh to recover session when possible,
          // unless backend explicitly asks us to skip refresh via header.
          this.data.showAjaxRequestLoader = false;

          if (skipRefresh) {
            // Backend asked to skip refresh — go straight to fakesplash
            this.router.navigateByUrl('/fakesplash');
            return EMPTY;
          }

          // If this request is an excluded route, do not show modal/extra handling
          if (isRouteExcluded(normalizedReqUrl)) {
            // simply redirect to fakesplash
            this.router.navigateByUrl('/fakesplash');
            return EMPTY;
          }

          // Try to refresh user from pulse endpoint in background
          try {
            const payload = { token: this.data.token || this.data.storage.getToken?.() || null };
            // call pulse; do not block the UI thread - subscribe and handle result
            void this.data.apiPost('pulse', payload).then(
              (resp: { user?: unknown; token?: string }) => {
                if (resp && resp.user) {
                  this.data.user = resp.user;
                  if (resp.token) {
                    this.data.token = resp.token;
                  }
                  this.data.isUserLogged = !!this.data.token;
                  console.log('Session refreshed from interceptor pulse call.');
                } else {
                  this.router.navigateByUrl('/fakesplash');
                }
              },
              (err) => {
                console.warn('Background pulse refresh failed:', err);
                this.router.navigateByUrl('/fakesplash');
              }
            );
          } catch (e) {
            // on any unexpected error route to fakesplash
            console.error('Error attempting background refresh:', e);
            this.router.navigateByUrl('/fakesplash');
          }

          // stop original request pipeline
          return EMPTY;

        } else if (request.url.indexOf('getubetter.com') == -1) {
          return throwError(() => error);
        }
        
        if (error.status == 503) { // Maintenance mode
          return throwError(() => error);
        } else if (this.data.maintenance_mode == true && error.status < 500) {
          this.data.maintenance_mode = false;
        }

        if (error.error != undefined && error.error.app_update != undefined && request.url.indexOf('pulse') != -1) {
          console.log('update available');
          this.data.goToUpdate(error.error.app_update.title, error.error.app_update.subtitle, error.error.app_update.force);
        } else {

          // honor X-Ignore-Alert header: do not show modal when present
          if (!ignoreAlert &&
            !request.url.includes('device-') &&
            !request.url.includes('feedback') && // exclude device registration and feedback endpoints from error modals, as they can fail in expected ways (e.g. user denies permissions) and we don't want to show modals in those cases
            !request.url.includes('webauthn-list') &&
            !request.url.includes('userappnav') &&
            !request.url.includes('pulse') &&
            !request.url.includes('login') &&
            !request.url.includes('/health') &&

            error.status != 400 && error.status != 555 && error.status != 401 && error.status != 403) {
            this.data.showAjaxRequestLoader = false;

            // Session-expired guest warning already owns the UX — do not stack App Support.
            const errorHandling = this.injector.get(ErrorHandlingService);
            if (errorHandling.hasActiveCustomWarning(BackendErrorCode.SESSION_EXPIRED)) {
              return EMPTY;
            }

            // Show standardized contact/support modal for internal/unexpected errors
            try {
              if (error.status !== 500) {
                // For HTTP not 500, show a more specific message
                this.infoModal.showContactErrorModal('There was a problem connecting you to the server. Please try again.', 'Unable to connect you');
              } else {
                this.infoModal.showContactErrorModal();
              }
            } catch (e) {
              // Fallback to existing alert if modal service fails for any reason
              this.data.presentAlert('Unable to connect you', '', 'OK', 'There was a problem connecting you to the server. Please try again.');
            }

            if (
              this.data.diag != undefined &&
              this.data.diag.platform != undefined &&
              this.data.diag.platform !== null
            ) {
              clearTimeout(this.timer);
              let storageData = { data: {}, error: '', stack: '' };
              Object.keys(localStorage).forEach(data => {
                if (['pathwaylabels', 'introslider', 'showAjaxRequestLoader', 'token', 'colors', 'first_app_open'].indexOf(data) == -1) {
                  try {
                    storageData.data[data] = JSON.parse(localStorage.getItem(data));
                  } catch (err) {
                    storageData.data[data] = localStorage.getItem(data);
                  }
                }
              });

              storageData.error = (this.data.user != undefined && this.data.user.id > 0 ? 'Uid ' + this.data.user.id + ', ' : '') + 'Gid ' + this.data.guest_id + ' ' + error.message;
              storageData.stack = JSON.stringify(error);
              console.log('storageData: ', storageData);
              this.timer = setTimeout(() => {
                this.data.apiPost('saveerror', storageData).then(() => { });
              }, 3000);

            }

            return EMPTY;
          }
        }

        return throwError(() => error);
        }));
      })
    );
  }
}

const excludedRoutesFromAlert = [
  '/userappnav',
  '/usernavguest',
  '/fakesplash',
  '/accesscodes',
  '/login',
  '/pulse',
  '/device-*',
  '/webauthn-*'
];

/**
 * Backend error_codes that mean bad credentials / auth challenge — not an expired session.
 * Login page (and register/MFA) expect these 401s to be rethrown into their catch handlers.
 */
const CREDENTIAL_AUTH_ERROR_CODES = new Set([
  'incorrect_password',
  'email_not_found',
  'organisation_offboarded',
  'mfa_code_error',
]);

/**
 * API path fragments for pre-auth / credential endpoints.
 * Matched against the normalized request path (e.g. `/api/login`).
 */
const CREDENTIAL_AUTH_PATH_HINTS = [
  '/api/login',
  '/login',
  '/api/signup',
  '/signup',
  '/api/forgot',
  '/forgot',
  '/api/generatemfa',
  '/api/validatemfa',
  '/api/generateresetmfa',
  '/api/verifyresetcode',
  '/api/nhs/login',
  '/api/nhs/statecode/info',
  '/api/nhs/statecode/update',
  '/api/nhs/statecode/create',
  '/api/nhs/statecode/delete',
];

/** 401 payloads from middleware (error_code) or legacy pulse (error). */
export function isAccountRevalidationRequired(error: HttpErrorResponse): boolean {
  const payload = error?.error;
  return payload?.error_code === 'revalidation_required'
    || payload?.error === 'revalidation_required';
}

/**
 * True when the active route is the guest landing page (query strings allowed).
 * 401s while already on /guest are not treated as session expiry.
 */
export function isOnGuestPage(routerUrl: string): boolean {
  const path = (routerUrl || '').split('?')[0].split('#')[0];
  return path === '/guest' || path.startsWith('/guest/');
}

/**
 * True when a 401 is a login/register/MFA credential failure that must reach page UI,
 * not the guest Session expired warning.
 */
export function isCredentialAuthFailure(
  error: HttpErrorResponse,
  normalizedUrl: string,
  rawUrl: string = '',
): boolean {
  const code = error?.error?.error_code;
  if (typeof code === 'string' && CREDENTIAL_AUTH_ERROR_CODES.has(code)) {
    return true;
  }

  const path = (normalizedUrl || normalizeUrl(rawUrl) || '').toLowerCase();
  const raw = (rawUrl || '').toLowerCase();
  return CREDENTIAL_AUTH_PATH_HINTS.some(
    (hint) => path === hint || path.endsWith(hint) || path.includes(hint) || raw.includes(hint),
  );
}

/** Drop host fields. Responses must not change where the app sends requests. */
function flagsOnly(body: any): any {
  if (!body || typeof body !== 'object') {
    return body;
  }
  const copy = { ...body };
  delete copy.api_url;
  delete copy.asset_url;
  if (copy.settings && typeof copy.settings === 'object' && !Array.isArray(copy.settings)) {
    const settings = { ...copy.settings };
    delete settings.api_url;
    delete settings.asset_url;
    copy.settings = settings;
  }
  return copy;
}

function normalizeUrl(rawUrl: string, basePath: string = ''): string {
  try {
    // remove leading/trailing whitespace
    let url = (rawUrl || '').trim();

    // if it's an absolute URL, remove protocol + host
    // handle URLs like https://testing.getubetter.com/api/test-url-xxx?foo=1
    const doubleSlashIdx = url.indexOf('//');
    if (doubleSlashIdx !== -1) {
      // remove protocol and host up to the first single slash after host
      const afterHostIdx = url.indexOf('/', doubleSlashIdx + 2);
      url = afterHostIdx !== -1 ? url.substring(afterHostIdx) : '/';
    }

    // remove query params and fragments
    url = url.split('?')[0].split('#')[0];

    // remove configured basePath prefix if present
    if (basePath && url.startsWith(basePath)) {
      url = url.substring(basePath.length);
    }

    // ensure starts with single '/'
    if (!url.startsWith('/')) {
      url = '/' + url;
    }

    // trim trailing slashes (but keep single '/')
    if (url.length > 1 && url.endsWith('/')) {
      url = url.replace(/\/+$/, '');
    }

    return url.toLowerCase();
  } catch (e) {
    return (rawUrl || '').toLowerCase();
  }
}

export function isRouteExcluded(url: string): boolean {
  // Normalize incoming url and compare to excluded patterns
  const normalized = normalizeUrl(url);
  return excludedRoutesFromAlert.some(route => {
    if (route.includes('*')) {
      const baseRoute = route.replace('*', '').toLowerCase();
      return normalized.includes(baseRoute);
    }
    return normalized === route.toLowerCase();
  });
}

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  timer: any;
  private readonly moduleLoadRecoveryKey = 'gub.module-load-recovery';
  private readonly moduleLoadRecoveryWindowMs = 15000;

  constructor(
    private injector: Injector,
  ) {
  }

  async handleError(error: any) {
    console.log('=== Global Error Handler ===');
    console.log('Error:', error);
    
    // Enhanced error logging for development
    if (error?.stack) {
      console.log('Stack trace:', error.stack);
    }
    if (error?.source) {
      console.log('Error source:', error.source);
    }
    if (error?.filename) {
      console.log('Error filename:', error.filename);
    }
    if (error?.lineno) {
      console.log('Error line number:', error.lineno);
    }
    if (error?.colno) {
      console.log('Error column number:', error.colno);
    }

    if (this.tryRecoverModuleLoad(error)) {
      return;
    }
    
    // Get data service
    let dataService = this.injector.get(DataService);
    
    // ⚠️ CRITICAL: Detect null user data errors and attempt recovery
     const errorMessage = error?.message || '';
     const hasToken = !!dataService.token;
     const storedUser = await dataService.storage.getUser();
     const userIsNull = !storedUser;

     // Hydrate in-memory user only when we actually have persisted user data.
     // Do not force-set user on every error, as this can trigger side effects during routing.
     if (!dataService.user && !!storedUser) {
      dataService.user = storedUser;
     }
    
    // Check if error is related to missing user data (common patterns)
    const isUserDataError = 
      errorMessage.includes('Cannot read property') && (errorMessage.includes("'user'") || errorMessage.includes('of null')) ||
      errorMessage.includes('Cannot read properties of null') ||
      errorMessage.includes('Cannot read properties of undefined') && errorMessage.includes('user') ||
      errorMessage.includes('user is null') ||
      errorMessage.includes('user is undefined');
    
    if (isUserDataError && hasToken && userIsNull) {
      console.error('🚨 NULL USER DATA ERROR DETECTED - Token exists but user is null');
      console.log('🔄 Attempting automatic user recovery...');
      
      // Trigger silent recovery (won't cause loops due to flag in silentUserRecovery)
      dataService.silentUserRecovery();
      
      // Don't throw the error - let the recovery attempt complete
      return;
    }
    
    // Check if it's an error from an HTTP response
    if (
      dataService.diag != undefined &&
      dataService.diag.platform != undefined &&
      dataService.diag.platform !== null
    ) {
      if (
        error.message != undefined &&
        (
          error.message.indexOf('User denied permissions') == -1 &&
          error.message.indexOf('User cancelled photos app') == -1
        )
      ) {
        clearTimeout(this.timer);
        console.log('globalerrorhandler: ', error);
        console.log('globalerrorhandler2 : ', dataService.diag.platform);
        
        // Preserve user data by avoiding localStorage clearing operations
        let storageData = { data: {}, error: '', stack: '' };
        
        // Only collect diagnostic data, don't modify localStorage
        const diagnosticKeys = ['pathwaylabels', 'introslider', 'showAjaxRequestLoader', 'colors', 'first_app_open'];
        Object.keys(localStorage).forEach(data => {
          if (diagnosticKeys.indexOf(data) !== -1) {
            try {
              storageData.data[data] = JSON.parse(localStorage.getItem(data));
            } catch (err) {
              storageData.data[data] = localStorage.getItem(data);
            }
          }
        });

        // Preserve critical user session data by not including in error report
        const isUserLogged = dataService.isUserLogged;
        const userId = dataService.user?.id;
        const guestId = dataService.guest_id;

        storageData.error = (userId > 0 ? 'Uid ' + userId + ', ' : '') + 'Gid ' + guestId + ' ' + error.message;
        if (!storageData.error) return;
        storageData.stack = error.stack;
        console.log('storageData: ', storageData);
        
        // Report error without affecting user session
        this.timer = setTimeout(() => {
          // Ensure user data is still intact before sending error report
          if (dataService.isUserLogged && dataService.user) {
            dataService.apiPost('saveerror', storageData).then(() => { 
              console.log('Error reported successfully, user session preserved');
            }).catch((reportError) => {
              console.error('Failed to report error:', reportError);
            });
          }
        }, 3000);
      }
    } else {
      throw error;
    }
  }

  private tryRecoverModuleLoad(error: any): boolean {
    const errorMessage = this.getModuleLoadErrorMessage(error);

    if (!this.isRecoverableModuleLoadError(errorMessage) || typeof window === 'undefined') {
      return false;
    }

    const lastAttempt = Number(window.sessionStorage.getItem(this.moduleLoadRecoveryKey) || '0');
    const now = Date.now();

    if (!lastAttempt || Number.isNaN(lastAttempt) || now - lastAttempt > this.moduleLoadRecoveryWindowMs) {
      window.sessionStorage.setItem(this.moduleLoadRecoveryKey, String(now));
      console.warn('Recovering from stale lazy module or optimized dependency chunk by reloading the page once.');
      window.location.reload();
      return true;
    }

    window.sessionStorage.removeItem(this.moduleLoadRecoveryKey);
    return false;
  }

  private isRecoverableModuleLoadError(errorMessage: string): boolean {
    return errorMessage.includes('Failed to fetch dynamically imported module')
      || errorMessage.includes('Importing a module script failed')
      || (errorMessage.includes('optimize deps directory') && errorMessage.includes('does not exist'));
  }

  private getModuleLoadErrorMessage(error: any): string {
    if (typeof error?.message === 'string') {
      return error.message;
    }

    if (typeof error?.rejection?.message === 'string') {
      return error.rejection.message;
    }

    if (typeof error === 'string') {
      return error;
    }

    return '';
  }
}